import { Component, EventEmitter, HostBinding, Input, Output, Attribute } from '@angular/core';
import { UserModel } from 'models/generated/Identity.Api.Models.User';
import { UserService } from 'services/user.service';

export type IconFamily = 'light' | 'regular' | 'solid';

export type IconType =
	'angle-double-down' |
	'angle-double-up' |
	'arrow-alt-from-top' |
	'arrow-up-right-from-square' |
	'arrow-right-arrow-left' |
	'balance-scale' |
	'bell' |
	'broom-wide' |
	'bug' |
	'building' |
	'calendar-alt' |
	'calendar-day' |
	'calendar-exclamation' |
	'calendar-times' |
	'chart-pie-simple-circle-dollar' |
	'check' |
	'chevron-down' |
	'chevron-right' |
	'chevron-up' |
	'circle-info' |
	'circle-question' |
	'circle-stop' |
	'circle-user' |
	'clipboard-check' |
	'clipboard-list' |
	'clipboard-question' |
	'clock' |
	'clock-rotate-left' |
	'coins' |
	'comment' |
	'comment-plus' |
	'comments' |
	'comments-dollar' |
	'copy' |
	'desktop' |
	'door-open' |
	'door-closed' |
	'download' |
	'envelope' |
	'exchange' |
	'exclamation-triangle' |
	'external-link' |
	'eye-slash' |
	'face-raised-eyebrow' |
	'file' |
	'file-alt' |
	'file-archive' |
	'file-contract' |
	'file-edit' |
	'file-export' |
	'file-image' |
	'file-pdf' |
	'file-signature' |
	'file-video' |
	'file-word' |
 	'file-zipper' |
	'filter' |
	'filter-slash' |
	'folder' |
	'folder-open' |
	'folder-tree' |
	'globe' |
	'globe-pointer' |
	'hand-holding-dollar' |
	'handshake-alt-slash' |
	'handshake-simple' |
	'history' |
	'hourglass-half' |
	'house' |
	'key' |
	'layer-group' |
	'link-horizontal' |
	'list-check' |
	'lock' |
	'logo' |
	'memo-circle-check' |
	'paper-plane' |
	'pen-to-square' |
	'phone' |
	'plus' |
	'print-magnifying-glass' |
	'print-slash' |
	'refresh' |
	'reply' |
	'right' |
	'rocket-launch' |
	'sack-dollar' |
	'search' |
	'section' |
	'shopping-cart' |
	'signal-slash' |
	'square-check' |
	'square-full' |
	'square-info' |
	'square-plus' |
	'star' |
	'stream' |
	'suitcase' |
	'times' |
	'trash' |
	'trash-alt' |
	'trash-can-clock' |
	'tv' |
	'up-down' |
	'upload' |
	'user' |
	'users' |
	'user-edit' |
	'user-gear' |
	'user-headset' |
	'user-plus' |
	'user-robot' |
	'user-slash' |
	'xmark' |
	'wordpress';

@Component({
	selector: 'icon',
	template: '@if (badgeText) {<div class="badge">{{ badgeText }}</div>}',
	styleUrls: ['./icon.component.scss', './svg-icons.scss']
})
export class IconComponent {

	private iconMap: { [key: string]: string } = {
		'refresh': 'fa-arrows-rotate',
	};
	private lightIcons: IconType[] = ['bell', 'calendar-alt', 'clock', 'folder-tree', 'globe', 'globe-pointer'];
	private regularIcons: IconType[] = [
		'arrow-right-arrow-left',
		'chart-pie-simple-circle-dollar', 'circle-info', 'circle-question', 'circle-stop', 'clipboard-check', 'clipboard-list', 'clipboard-question', 'coins',
		'comment', 'comments', 'comments-dollar', 'comment-plus', 'copy',
		'download',
		'envelope',
		'face-raised-eyebrow', 'file', 'file-archive', 'file-contract', 'file-image', 'file-pdf', 'file-video', 'file-word', 'file-zipper', 'folder-open',
		'hand-holding-dollar', 'handshake-simple', 'house',
		'lock',
		'memo-circle-check',
		'paper-plane', 'print-magnifying-glass', 'print-slash',
		'refresh',
		'sack-dollar', 'search', 'shopping-cart', 'square-info',
		'trash-alt', 'trash-can-clock',
		'user-headset'
	];
	private svgIcons: IconType[] = ['logo', 'wordpress'];

	private _family?: IconFamily;
	@Input() set family(value: IconFamily | undefined) {
		this._family = value;
	}
	get family() {
		return this._family;
	}

	private _badgeText?: string;
	@Input() set badge(value: string | undefined) {
		this._badgeText = value;
	}
	@Input("badge-attention") set badgeAttention(value: string | undefined) {
		this._badgeText = value;
	}
	get badgeText() {
		return this._badgeText;
	}

	private imageUrl?: string;
	private isUserIcon = false;
	@Input() set userIcon(user: UserModel | undefined) {
		this.isUserIcon = !!user;
		this.imageUrl = undefined;
		if (user) this.userService.getProfileImageUrlByUserId(user.id).then(url => this.imageUrl = url);
	}

	@Output() click: EventEmitter<any> = new EventEmitter<any>();
	@HostBinding('attr.clickable') get clickable() { return this.click.observers.length > 0 ? '' : null };

	@Input() type: IconType = 'circle-question';

	@HostBinding('class') get class() {
		if (this.isUserIcon) return 'user-icon';
		if (this.svgIcons.includes(this.type)) return `svgicon svg-${this.type}`;

		// Font-Awesome icon:
		const styleClass = this.family ? 'fa-' + this.family : this.lightIcons.includes(this.type) ? 'fa-light' : this.regularIcons.includes(this.type) ? 'fa-regular' : 'fa-solid';
		const iconClass = this.iconMap[this.type] || `fa-${this.type}`;
		return `${styleClass} ${iconClass}`;
	}
	@HostBinding('style.background-image') get backgroundImage() {
		if (!this.imageUrl) return null;

		return `url(${this.imageUrl})`;
	}

	@HostBinding('attr.badge') get badgeAttribute() { return this.badgeText ? '' : null; }

	constructor(private readonly userService: UserService, @Attribute('add') addAttribute: string) {
		if (addAttribute !== null) {
			this._badgeText = '+';
		}
	}

}
